import Phaser from 'phaser'
import StateMachine from '../statemachine/StateMachine'
import {
	sharedInstance as events,
	getPersistentBudgetState,
	setPersistentBudgetState,
	clearPersistentBudgetState,
	clearPersistentPlayTimeState,
	getPersistentPlayTimeState,
	setPersistentGameOverSummary
} from './EventCenter'
import ObstaclesController from './ObstaclesController'

type CursorKeys = Phaser.Types.Input.Keyboard.CursorKeys

type ThrownCard = {
	projectile: Phaser.Physics.Matter.Image
	startX: number
	maxDistance: number
	hitIds: Set<number>
	isDespawning: boolean
}

export type PlayerBudgetState = {
	current: number
	max: number
	hasCardPowerup?: boolean
}

const PLAYER_SPRITESHEET_COLUMNS = 7
const BOOMERANG_OUT_SPEED = 12
const BOOMERANG_MAX_DISTANCE = 450
const CARD_DESPAWN_FADE_DURATION_MS = 180
const CARD_ALLOW_SIMULTANEOUS_THROWS = true
const PLAYER_HIT_ANIMATION_DURATION_MS = 520
const PLAYER_HIT_KNOCKBACK_PIXELS = 78
const PLAYER_HIT_KNOCKBACK_DURATION_MS = 120
const PLAYER_LANDING_FRAME_HOLD_MS = 90
const PLAYER_LANDING_TOP_TOLERANCE_PX = 8
const PLAYER_LANDING_BOTTOM_PENETRATION_TOLERANCE_PX = 42
const PLAYER_LANDING_MIN_HORIZONTAL_OVERLAP_RATIO = 0.35
const PLAYER_LANDING_MIN_HORIZONTAL_OVERLAP_RATIO_STATIC_EDGE = 0.08
const PLAYER_JUMP_ROW_INDEX = 4
const PLAYER_JUMP_LANDING_FRAME_OFFSET = 5
const PLAYER_JUMP_VELOCITY = -11.5
const SECRET_DOUBLE_JUMP_VELOCITY = -12.6
const SECRET_DOUBLE_JUMP_ARM_WINDOW_MS = 700
const SECRET_DOUBLE_JUMP_TRIGGER_WINDOW_MS = 320
const INITIAL_BUDGET = 3000
const COIN_BUDGET_GAIN = 50
const DEFAULT_COIN_REWARD_TYPE = 'bizum'
const DEFAULT_ENEMY_HIT_BUDGET_LOSS = 200
const BOOMERANG_CARD_TEXTURE_KEY = 'boomerang-card'
const BOOMERANG_CARD_WIDTH = 23
const BOOMERANG_CARD_HEIGHT = 14
const CARD_POWERUP_TYPE = 'card-powerup'
const CARD_POWERUP_CONFIG = {
	sizeMultiplier: 2.2,
	rangeMultiplier: 1.5
} as const
const BOOMERANG_SPAWN_OFFSET_X = 36
const BOOMERANG_SPAWN_OFFSET_Y = 0
const PLAYER_COLLIDER_WIDTH_RATIO = 0.40
const PLAYER_COLLIDER_HEIGHT_RATIO = 0.8
const PLAYER_COLLIDER_BOTTOM_OFFSET = 0
const PLAYER_COLLIDER_CHAMFER_RATIO = 0
const PLAYER_COLLIDER_CHAMFER_MAX_PX = 0
const PLAYER_COIN_ANIMATION_FRAME_RATE = 16
const PLAYER_COIN_ANIMATION_FRAMES = 3
const PLAYER_COIN_ANIMATION_REPEATS = 3
const PLAYER_COIN_RIGHT_ROW_1_BASED = 13
const PLAYER_COIN_LEFT_ROW_1_BASED = 14
const PLAYER_COIN_SINGLE_FRAME_HOLD_MS = 120
const PLAYER_STOMP_COLLISION_GRACE_MS = 180
const PLAYER_IDLE_STABLE_SURFACE_MAX_SPEED_Y = 0.2
const GROUND_COLLISION_CATEGORY = 0x0002
const DEBUG_ENEMY_COLLISION_LOG = true
const DEFAULT_ACTION_SFX_VOLUME = 0.45

export default class PlayerController
{
	private scene: Phaser.Scene
	private sprite: Phaser.Physics.Matter.Sprite
	private cursors: CursorKeys
	private keyA: Phaser.Input.Keyboard.Key
	private keyD: Phaser.Input.Keyboard.Key
	private keyW: Phaser.Input.Keyboard.Key
	private keyS: Phaser.Input.Keyboard.Key
	private obstacles: ObstaclesController

	private stateMachine: StateMachine
	private budget = INITIAL_BUDGET
	private maxBudget = INITIAL_BUDGET

	private lastStompableEnemy?: Phaser.Physics.Matter.Sprite
	private activeCards: ThrownCard[] = []
	private coinAnimRestoreEvent?: Phaser.Time.TimerEvent
	private jumpLandingEvent?: Phaser.Time.TimerEvent
	private lastStompTimestampMs = -Infinity
	private pendingEnemyHitBudgetLoss = DEFAULT_ENEMY_HIT_BUDGET_LOSS
	private pendingEnemyHitEnemyType = 'unknown'
	private hasCardPowerup = false
	private jumpStartedAtMs = -Infinity
	private secretDoubleJumpArmedAtMs = -Infinity
	private secretDoubleJumpArmed = false
	private secretDoubleJumpUsed = false

	private debugEnemyCollisionLog(stage: string, body: MatterJS.BodyType, extra: Record<string, unknown> = {})
	{
		if (!DEBUG_ENEMY_COLLISION_LOG)
		{
			return
		}

		const enemySprite = body.gameObject as Phaser.Physics.Matter.Sprite | undefined
		// console.info('[temp-enemy-collision-debug]', {
		// 	stage,
		// 	playerState: this.stateMachine?.previousStateName,
		// 	player: {
		// 		x: this.sprite.x,
		// 		y: this.sprite.y,
		// 		velocityX: (this.sprite.body as MatterJS.BodyType)?.velocity?.x,
		// 		velocityY: (this.sprite.body as MatterJS.BodyType)?.velocity?.y
		// 	},
		// 	enemy: {
		// 		bodyId: body.id,
		// 		isSensor: body.isSensor,
		// 		isStatic: body.isStatic,
		// 		hasGameObject: Boolean(body.gameObject),
		// 		spriteActive: enemySprite?.active,
		// 		spriteVisible: enemySprite?.visible,
		// 		isDeadData: enemySprite?.getData?.('isDead'),
		// 		animKey: enemySprite?.anims?.currentAnim?.key ?? null,
		// 		x: enemySprite?.x,
		// 		y: enemySprite?.y
		// 	},
		// 	extra
		// })
	}

	private isEnemyAlreadyNeutralized(body: MatterJS.BodyType): boolean
	{
		const enemySprite = body.gameObject as Phaser.Physics.Matter.Sprite | undefined
		if (!enemySprite || !enemySprite.active)
		{
			return true
		}

		if (body.isSensor)
		{
			return true
		}

		if (enemySprite.getData('isDead'))
		{
			return true
		}

		const currentAnimationKey = enemySprite.anims?.currentAnim?.key ?? ''
		if (currentAnimationKey.includes('death'))
		{
			return true
		}

		return false
	}

	private neutralizeEnemyImmediately(enemy: Phaser.Physics.Matter.Sprite)
	{
		if (!enemy.active)
		{
			return
		}

		enemy.setData('isDead', true)
		enemy.setSensor(true)
	}

	private playConfiguredSfx(registryKey: string)
	{
		const soundKey = this.scene.game.registry.get(registryKey)
		if (typeof soundKey !== 'string' || soundKey.length === 0)
		{
			return
		}

		if (!this.scene.cache.audio.exists(soundKey))
		{
			return
		}

		const configuredVolume = this.scene.game.registry.get('sfxVolume')
		const volume = typeof configuredVolume === 'number'
			? Phaser.Math.Clamp(configuredVolume, 0, 1)
			: DEFAULT_ACTION_SFX_VOLUME

		this.scene.sound.play(soundKey, { volume })
	}

	private resolveEnemyType(enemySprite: Phaser.Physics.Matter.Sprite | undefined): string
	{
		const textureKey = (enemySprite?.texture?.key ?? '').toLowerCase()
		if (textureKey.includes('tpv-volador') || textureKey.includes('escaner'))
		{
			return 'tpv_volador'
		}

		if (textureKey.includes('cajero-verde'))
		{
			return 'cajero_verde'
		}

		if (textureKey.includes('cajero-amarillo'))
		{
			return 'cajero_amarillo'
		}

		if (textureKey.includes('final-boss'))
		{
			return 'final_boss'
		}

		return 'unknown'
	}

	constructor(
		scene: Phaser.Scene,
		sprite: Phaser.Physics.Matter.Sprite,
		cursors: CursorKeys,
		obstacles: ObstaclesController,
		initialBudgetState?: PlayerBudgetState
	)
	{
		this.scene = scene
		this.sprite = sprite
		this.cursors = cursors
		this.keyA = this.scene.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.A)
		this.keyD = this.scene.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.D)
		this.keyW = this.scene.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.W)
		this.keyS = this.scene.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.S)
		this.obstacles = obstacles
		this.applyInitialBudgetState(initialBudgetState)
		this.applyConfiguredCollider()

		this.createAnimations()
		this.emitBudgetChanged()

		this.stateMachine = new StateMachine(this, 'player')

		this.stateMachine.addState('idle', {
			onEnter: this.idleOnEnter,
			onUpdate: this.idleOnUpdate
		})
		.addState('walk', {
			onEnter: this.walkOnEnter,
			onUpdate: this.walkOnUpdate,
			onExit: this.walkOnExit
		})
		.addState('jump', {
			onEnter: this.jumpOnEnter,
			onUpdate: this.jumpOnUpdate
		})
		.addState('spike-hit', {
			onEnter: this.spikeHitOnEnter
		})
		.addState('enemy-hit', {
			onEnter: this.enemyHitOnEnter
		})
		.addState('enemy-stomp', {
			onEnter: this.enemyStompOnEnter
		})
		.addState('dead', {
			onEnter: this.deadOnEnter
		})
		.setState('idle')

		this.sprite.on(Phaser.Animations.Events.ANIMATION_COMPLETE, (animation: Phaser.Animations.Animation) => {
			if (animation.key !== 'player-throw')
			{
				return
			}

			if (this.stateMachine.isCurrentState('jump'))
			{
				this.sprite.play('player-jump')
				return
			}

			if (this.stateMachine.isCurrentState('walk'))
			{
				this.sprite.play('player-walk')
				return
			}

			this.sprite.play('player-idle')
		})

		this.sprite.setOnCollide((data: MatterJS.ICollisionPair) => {
			const now = this.scene.time.now
			const playerBody = this.sprite.body as MatterJS.BodyType
			if (!playerBody || !data.bodyA || !data.bodyB)
			{
				return
			}
			const body = (data.bodyA === playerBody ? data.bodyB : data.bodyA) as MatterJS.BodyType
			if (!body)
			{
				return
			}
			const isEnemyObstacle = this.obstacles.is('tpv-volador', body)
				|| this.obstacles.is('cajero', body)
				|| this.obstacles.is('final-boss', body)
			const enemySprite = body.gameObject as Phaser.Physics.Matter.Sprite | undefined

			if (isEnemyObstacle)
			{
				this.debugEnemyCollisionLog('on-collide-enemy-detected', body, {
					now,
					msSinceLastStomp: now - this.lastStompTimestampMs
				})
			}

			if (isEnemyObstacle && (!enemySprite || !enemySprite.active))
			{
				this.debugEnemyCollisionLog('ignored-inactive-or-missing-sprite', body)
				return
			}

			if (isEnemyObstacle && (now - this.lastStompTimestampMs) <= PLAYER_STOMP_COLLISION_GRACE_MS)
			{
				this.debugEnemyCollisionLog('ignored-stomp-grace-window', body)
				return
			}

			if (isEnemyObstacle && this.isEnemyAlreadyNeutralized(body))
			{
				this.debugEnemyCollisionLog('ignored-enemy-neutralized', body)
				return
			}

			const collidedSprite = body.gameObject as { getData?: (key: string) => unknown } | undefined

			if (this.obstacles.is('spikes', body))
			{
				this.stateMachine.setState('spike-hit')
				return
			}

			if (this.obstacles.is('street-furniture', body))
			{
				this.tryHandleLanding(body)
				return
			}

			if (isEnemyObstacle)
			{
				this.lastStompableEnemy = body.gameObject as Phaser.Physics.Matter.Sprite
				const canStompEnemies = this.scene.game.registry.get('canStompEnemies') !== false
				if (canStompEnemies && this.sprite.y < body.position.y)
				{
					this.debugEnemyCollisionLog('state-change-enemy-stomp', body)
					this.stateMachine.setState('enemy-stomp')
				}
				else
				{
					this.debugEnemyCollisionLog('state-change-enemy-hit', body)
					this.pendingEnemyHitBudgetLoss = this.resolveEnemyHitBudgetLoss(enemySprite)
					this.pendingEnemyHitEnemyType = this.resolveEnemyType(enemySprite)
					this.stateMachine.setState('enemy-hit')
				}
				return
			}

			const gameObject = body.gameObject

			if (!gameObject)
			{
				return
			}

			if (gameObject instanceof Phaser.Physics.Matter.TileBody)
			{
				this.tryHandleLanding(body)
				return
			}

			const sprite = gameObject as { getData?: (key: string) => unknown, destroy?: () => void }
			if (typeof sprite.getData !== 'function')
			{
				return
			}

			const type = sprite.getData('type')

			switch (type)
			{
				case 'health':
				{
					const rawValue = sprite.getData('healthPoints')
					const value = typeof rawValue === 'number' ? rawValue : 10
					this.setBudget(this.budget + value)
					events.emit('health-picked', {
						amount: value
					})
					if (typeof sprite.destroy === 'function')
					{
						sprite.destroy()
					}
					break
				}

				case 'coin':
				{
					this.collectCoin(sprite as Phaser.Physics.Matter.Sprite)
					break
				}

					case CARD_POWERUP_TYPE:
					{
						this.collectCardPowerup(sprite as Phaser.Physics.Matter.Sprite)
						break
					}
			}
		})

		this.sprite.setOnCollideActive((data: MatterJS.ICollisionPair) => {
			if (!this.stateMachine.isCurrentState('jump'))
			{
				return
			}

			const playerBody = this.sprite.body as MatterJS.BodyType
			if (!playerBody || !data.bodyA || !data.bodyB)
			{
				return
			}
			const body = (data.bodyA === playerBody ? data.bodyB : data.bodyA) as MatterJS.BodyType
			if (!body || body.isSensor)
			{
				return
			}

			if (this.obstacles.is('street-furniture', body))
			{
				this.tryHandleLanding(body)
				return
			}

			const gameObject = body.gameObject
			if (gameObject instanceof Phaser.Physics.Matter.TileBody)
			{
				this.tryHandleLanding(body)
			}
		})
	}

	getBudgetState(): PlayerBudgetState
	{
		return {
			current: this.budget,
			max: this.maxBudget,
			hasCardPowerup: this.hasCardPowerup
		}
	}

	private applyInitialBudgetState(initialBudgetState?: PlayerBudgetState)
	{
		const budgetState = initialBudgetState ?? getPersistentBudgetState()
		if (!budgetState)
		{
			return
		}

		const safeMax = Math.max(1, Math.round(budgetState.max))
		const safeCurrent = Phaser.Math.Clamp(Math.round(budgetState.current), 0, safeMax)
		this.maxBudget = safeMax
		this.budget = safeCurrent
		this.hasCardPowerup = budgetState.hasCardPowerup === true
		this.persistBudgetState()
	}

	private persistBudgetState()
	{
		setPersistentBudgetState({
			current: this.budget,
			max: this.maxBudget,
			hasCardPowerup: this.hasCardPowerup
		})
	}

	private applyConfiguredCollider()
	{
		const sourceWidth = this.sprite.displayWidth
		const sourceHeight = this.sprite.displayHeight

		const colliderWidth = Math.max(1, sourceWidth * PLAYER_COLLIDER_WIDTH_RATIO)
		const colliderHeight = Math.max(1, sourceHeight * PLAYER_COLLIDER_HEIGHT_RATIO)
		const colliderChamferRadius = Phaser.Math.Clamp(
			Math.min(colliderWidth, colliderHeight) * PLAYER_COLLIDER_CHAMFER_RATIO,
			0,
			PLAYER_COLLIDER_CHAMFER_MAX_PX
		)

		const body = this.scene.matter.bodies.rectangle(
			this.sprite.x,
			this.sprite.y,
			colliderWidth,
			colliderHeight,
			{
				chamfer: {
					radius: colliderChamferRadius
				}
			}
		)

		this.sprite.setExistingBody(body)
		// Reduce wall friction so touching lateral tiles does not dampen fall speed.
		this.sprite.setFriction(0, 0.01, 0)
		this.sprite.setFixedRotation()
		this.alignSpriteToCollider()
	}

	private alignSpriteToCollider()
	{
		const sourceWidth = this.sprite.displayWidth
		const sourceHeight = this.sprite.displayHeight
		const colliderHeight = Math.max(1, sourceHeight * PLAYER_COLLIDER_HEIGHT_RATIO)
		const desiredOriginY = sourceHeight - PLAYER_COLLIDER_BOTTOM_OFFSET - (colliderHeight * 0.5)

		this.sprite.setDisplayOrigin(sourceWidth * 0.5, desiredOriginY)
	}

	private collectCoin(coin: Phaser.Physics.Matter.Sprite)
	{
		if (!coin.active)
		{
			return
		}

		const coinBudgetRaw = coin.getData('budgetValue')
		const coinBudget = typeof coinBudgetRaw === 'number'
			? coinBudgetRaw
			: COIN_BUDGET_GAIN
		const coinRewardTypeRaw = coin.getData('coinRewardType')
		const coinRewardType = typeof coinRewardTypeRaw === 'string' && coinRewardTypeRaw.trim().length > 0
			? coinRewardTypeRaw.trim().toLowerCase()
			: DEFAULT_COIN_REWARD_TYPE

		coin.destroy()

		if (this.budget >= this.maxBudget)
		{
			this.maxBudget += coinBudget
		}

		this.setBudget(this.budget + coinBudget)
		events.emit('coin-collected', {
			amount: coinBudget,
			rewardType: coinRewardType
		})
		this.playConfiguredSfx('sfxKeyCoinCollected')

		this.playCoinPickupAnimation()
	}

	private collectCardPowerup(powerup: Phaser.Physics.Matter.Sprite)
	{
		if (!powerup.active)
		{
			return
		}

		const floatTween = powerup.getData('floatTween') as Phaser.Tweens.Tween | undefined
		if (floatTween?.isPlaying())
		{
			floatTween.stop()
		}

		powerup.destroy()
		this.playConfiguredSfx('sfxKeyCardPowerupCollected')

		if (this.hasCardPowerup)
		{
			this.playCoinPickupAnimation()
			return
		}

		this.hasCardPowerup = true
		this.persistBudgetState()
		this.playCoinPickupAnimation()
	}

	private playCoinPickupAnimation()
	{
		const wasFacingLeft = this.sprite.flipX
		this.sprite.flipX = false

		const coinRowIndex = wasFacingLeft
			? (PLAYER_COIN_LEFT_ROW_1_BASED - 1)
			: (PLAYER_COIN_RIGHT_ROW_1_BASED - 1)
		const secondFrameIndex = (coinRowIndex * PLAYER_SPRITESHEET_COLUMNS) + 1
		this.sprite.setFrame(secondFrameIndex)

		if (this.coinAnimRestoreEvent)
		{
			this.coinAnimRestoreEvent.remove(false)
		}

		this.coinAnimRestoreEvent = this.scene.time.delayedCall(PLAYER_COIN_SINGLE_FRAME_HOLD_MS, () => {
			this.sprite.flipX = wasFacingLeft

			if (this.stateMachine.isCurrentState('jump'))
			{
				this.sprite.play('player-jump', true)
				return
			}

			if (this.stateMachine.isCurrentState('walk'))
			{
				this.sprite.play('player-walk', true)
				return
			}

			this.sprite.play('player-idle', true)
			this.coinAnimRestoreEvent = undefined
		})
	}

	update(dt: number)
	{
		this.alignSpriteToCollider()
		this.handleBoomerangInput()
		this.updateCards(dt)
		this.stateMachine.update(dt)
	}

	private handleBoomerangInput()
	{
		if (!Phaser.Input.Keyboard.JustDown(this.cursors.space))
		{
			return
		}

		if (!CARD_ALLOW_SIMULTANEOUS_THROWS && this.activeCards.length > 0)
		{
			return
		}

		this.throwCard()
	}

	private throwCard()
	{
		const direction: 1 | -1 = this.sprite.flipX ? -1 : 1
		const spawnX = this.sprite.x + (direction * BOOMERANG_SPAWN_OFFSET_X)
		const spawnY = this.sprite.y + BOOMERANG_SPAWN_OFFSET_Y
		const sizeMultiplier = this.hasCardPowerup ? CARD_POWERUP_CONFIG.sizeMultiplier : 1
		const rangeMultiplier = this.hasCardPowerup ? CARD_POWERUP_CONFIG.rangeMultiplier : 1
		const projectileWidth = BOOMERANG_CARD_WIDTH * sizeMultiplier
		const projectileHeight = BOOMERANG_CARD_HEIGHT * sizeMultiplier
		const projectileMaxDistance = BOOMERANG_MAX_DISTANCE * rangeMultiplier

		const projectile = this.scene.matter.add.image(spawnX, spawnY, BOOMERANG_CARD_TEXTURE_KEY, undefined, {
			isSensor: true,
			ignoreGravity: true
		}) as Phaser.Physics.Matter.Image

		projectile
			.setDisplaySize(projectileWidth, projectileHeight)
			.setDepth(this.sprite.depth)
			.setFixedRotation()
			.setFrictionAir(0)

		const card: ThrownCard = {
			projectile,
			startX: spawnX,
			maxDistance: projectileMaxDistance,
			hitIds: new Set<number>(),
			isDespawning: false
		}

		this.activeCards.push(card)

		projectile.setVelocity(direction * BOOMERANG_OUT_SPEED, 0)

		projectile.setOnCollide((data: MatterJS.ICollisionPair) => {
			if (card.isDespawning)
			{
				return
			}

			const projectileBody = projectile.body as MatterJS.BodyType
			if (!projectileBody || !data.bodyA || !data.bodyB)
			{
				return
			}
			const otherBody = (data.bodyA === projectileBody ? data.bodyB : data.bodyA) as MatterJS.BodyType
			if (!otherBody)
			{
				return
			}
			const otherGameObject = otherBody.gameObject
			const hitsTileGround = otherGameObject instanceof Phaser.Physics.Matter.TileBody
			const hitsBlockingFurniture = this.obstacles.is('street-furniture', otherBody)
			const hitsFinalBoss = this.obstacles.is('final-boss', otherBody)
			const enemy = otherBody.gameObject as Phaser.Physics.Matter.Sprite | undefined

			if (hitsTileGround || hitsBlockingFurniture)
			{
				this.startCardDespawn(card)
				return
			}

			if (hitsFinalBoss && enemy?.getData('damageImmune'))
			{
				return
			}

			if (!this.obstacles.is('tpv-volador', otherBody) && !this.obstacles.is('cajero', otherBody) && !hitsFinalBoss)
			{
				return
			}

			if (typeof otherBody.id !== 'number')
			{
				return
			}

			if (card.hitIds.has(otherBody.id))
			{
				return
			}

			card.hitIds.add(otherBody.id)
			if (enemy?.getData('isDead'))
			{
				this.startCardDespawn(card)
				return
			}

			if (hitsFinalBoss)
			{
				events.emit('final-boss-card-hit', {
					targetBodyId: otherBody.id
				})
				this.startCardDespawn(card)
				return
			}

			if (enemy)
			{
				this.debugEnemyCollisionLog('card-hit-enemy', otherBody)
				this.lastStompTimestampMs = this.scene.time.now
				this.neutralizeEnemyImmediately(enemy)
				this.playConfiguredSfx('sfxKeyEnemyDestroyed')

				if (this.obstacles.is('tpv-volador', otherBody))
				{
					events.emit('tpv-volador-stomped', enemy)
				}

				if (this.obstacles.is('cajero', otherBody))
				{
					events.emit('cajero-stomped', enemy)
				}
			}

			this.startCardDespawn(card)
		})

		this.sprite.play('player-throw', true)
	}

	private updateCards(dt: number)
	{
		for (let i = this.activeCards.length - 1; i >= 0; i--)
		{
			const card = this.activeCards[i]
			if (!card.projectile.active)
			{
				this.activeCards.splice(i, 1)
				continue
			}

			if (card.isDespawning)
			{
				continue
			}

			if (Math.abs(card.projectile.x - card.startX) >= card.maxDistance)
			{
				this.startCardDespawn(card)
			}
		}
	}

	private startCardDespawn(card: ThrownCard)
	{
		if (card.isDespawning || !card.projectile.active)
		{
			return
		}

		card.isDespawning = true
		card.projectile.setVelocity(0, 0)

		this.scene.tweens.add({
			targets: card.projectile,
			alpha: 0,
			duration: CARD_DESPAWN_FADE_DURATION_MS,
			onComplete: () => {
				card.projectile.destroy()
				this.removeCard(card)
			}
		})
	}

	private removeCard(cardToRemove: ThrownCard)
	{
		this.activeCards = this.activeCards.filter(card => card !== cardToRemove)
	}

	private setBudget(value: number)
	{
		this.budget = Phaser.Math.Clamp(value, 0, this.maxBudget)
		this.emitBudgetChanged()

		// TODO: check for death
		if (this.budget <= 0)
		{
			this.stateMachine.setState('dead')
		}
	}

	private emitBudgetChanged()
	{
		this.persistBudgetState()

		events.emit('budget-changed', {
			current: this.budget,
			max: this.maxBudget
		})
	}

	private clearCardPowerupOnDamage()
	{
		if (!this.hasCardPowerup)
		{
			return
		}

		this.hasCardPowerup = false
		this.persistBudgetState()
	}

	private applyEnemyBudgetDamage(amount: number = DEFAULT_ENEMY_HIT_BUDGET_LOSS)
	{
		this.setBudget(this.budget - amount)
	}

	private resolveEnemyHitBudgetLoss(enemySprite: Phaser.Physics.Matter.Sprite | undefined): number
	{
		if (!enemySprite)
		{
			return DEFAULT_ENEMY_HIT_BUDGET_LOSS
		}

		const rawDamage = enemySprite.getData('enemyHitBudgetLoss')
		if (typeof rawDamage !== 'number' || !Number.isFinite(rawDamage))
		{
			return DEFAULT_ENEMY_HIT_BUDGET_LOSS
		}

		return Math.max(0, rawDamage)
	}

	private idleOnEnter()
	{
		this.sprite.play('player-idle')
	}

	private idleOnUpdate()
	{
		const body = this.sprite.body as MatterJS.BodyType | undefined
		if (body)
		{
			const isAlmostStillVertically = Math.abs(body.velocity.y) <= PLAYER_IDLE_STABLE_SURFACE_MAX_SPEED_Y
			if (isAlmostStillVertically)
			{
				// Si no hay input y ya está apoyado, anulamos el deslizamiento residual.
				this.sprite.setVelocityX(0)
			}
		}

		if (this.isMoveLeftDown() || this.isMoveRightDown())
		{
			this.stateMachine.setState('walk')
		}

		const upJustPressed = this.isJumpJustPressed()
		if (upJustPressed)
		{
			this.stateMachine.setState('jump')
		}
	}

	private walkOnEnter()
	{
		this.sprite.play('player-walk')
	}

	private walkOnUpdate()
	{
		const speed = 5

		if (this.isMoveLeftDown())
		{
			this.sprite.flipX = true
			this.sprite.setVelocityX(-speed)
		}
		else if (this.isMoveRightDown())
		{
			this.sprite.flipX = false
			this.sprite.setVelocityX(speed)
		}
		else
		{
			this.sprite.setVelocityX(0)
			this.stateMachine.setState('idle')
		}

		const upJustPressed = this.isJumpJustPressed()
		if (upJustPressed)
		{
			this.stateMachine.setState('jump')
		}
	}

	private walkOnExit()
	{
		this.sprite.stop()
	}

	private jumpOnEnter()
	{
		this.jumpStartedAtMs = this.scene.time.now
		this.secretDoubleJumpArmedAtMs = -Infinity
		this.secretDoubleJumpArmed = false
		this.secretDoubleJumpUsed = false

		this.sprite.play('player-jump')
		this.sprite.setVelocityY(PLAYER_JUMP_VELOCITY)
		this.playConfiguredSfx('sfxKeyJump')
	}

	private playJumpLandingFrame()
	{
		if (this.jumpLandingEvent)
		{
			return
		}

		this.resetSecretDoubleJumpState()

		if (!this.isMoveLeftDown() && !this.isMoveRightDown())
		{
			this.sprite.setVelocityX(0)
		}

		const landingFrame = (PLAYER_JUMP_ROW_INDEX * PLAYER_SPRITESHEET_COLUMNS) + PLAYER_JUMP_LANDING_FRAME_OFFSET
		this.sprite.setFrame(landingFrame)

		this.jumpLandingEvent = this.scene.time.delayedCall(PLAYER_LANDING_FRAME_HOLD_MS, () => {
			this.jumpLandingEvent = undefined
			if (this.isMoveLeftDown() || this.isMoveRightDown())
			{
				this.stateMachine.setState('walk')
				return
			}

			this.stateMachine.setState('idle')
		})
	}

	private tryHandleLanding(otherBody: MatterJS.BodyType)
	{
		if (!this.stateMachine.isCurrentState('jump'))
		{
			return
		}

		if (!this.isLandingOnSurfaceBody(otherBody))
		{
			return
		}

		this.playJumpLandingFrame()
	}

	private isLandingOnSurfaceBody(otherBody: MatterJS.BodyType): boolean
	{
		const playerBody = this.sprite.body as MatterJS.BodyType
		if (!playerBody)
		{
			return false
		}

		if (playerBody.velocity.y < -0.5)
		{
			return false
		}

		const playerBottom = playerBody.bounds.max.y
		const playerTop = playerBody.bounds.min.y
		const playerWidth = playerBody.bounds.max.x - playerBody.bounds.min.x
		const surfaceTop = otherBody.bounds.min.y
		const tileIsBelowPlayerCenter = surfaceTop > playerBody.position.y
		const verticalDelta = playerBottom - surfaceTop
		const overlapLeft = Math.max(playerBody.bounds.min.x, otherBody.bounds.min.x)
		const overlapRight = Math.min(playerBody.bounds.max.x, otherBody.bounds.max.x)
		const horizontalOverlap = overlapRight - overlapLeft
		const minHorizontalOverlapRatio = otherBody.isStatic
			? PLAYER_LANDING_MIN_HORIZONTAL_OVERLAP_RATIO_STATIC_EDGE
			: PLAYER_LANDING_MIN_HORIZONTAL_OVERLAP_RATIO
		const minHorizontalOverlap = playerWidth * minHorizontalOverlapRatio

		if (!tileIsBelowPlayerCenter)
		{
			return false
		}

		if (horizontalOverlap < minHorizontalOverlap)
		{
			return false
		}

		const maxAllowedPenetration = Math.max(
			PLAYER_LANDING_BOTTOM_PENETRATION_TOLERANCE_PX,
			(playerBottom - playerTop) * 0.5
		)

		return verticalDelta >= -PLAYER_LANDING_TOP_TOLERANCE_PX
			&& verticalDelta <= maxAllowedPenetration
	}

	private jumpOnUpdate()
	{
		const speed = 5
		const movingLeft = this.isMoveLeftDown() && !this.isMoveRightDown()
		const movingRight = this.isMoveRightDown() && !this.isMoveLeftDown()
		this.tryHandleSecretDoubleJump()

		if (movingLeft)
		{
			this.sprite.flipX = true
			this.sprite.setVelocityX(-speed)
		}
		else if (movingRight)
		{
			this.sprite.flipX = false
			this.sprite.setVelocityX(speed)
		}
		else
		{
			this.sprite.setVelocityX(0)
		}
	}

	private tryHandleSecretDoubleJump()
	{
		if (this.secretDoubleJumpUsed)
		{
			return
		}

		const now = this.scene.time.now

		if (!this.secretDoubleJumpArmed)
		{
			if ((now - this.jumpStartedAtMs) > SECRET_DOUBLE_JUMP_ARM_WINDOW_MS)
			{
				return
			}

			if (this.isDownJustPressed())
			{
				this.secretDoubleJumpArmed = true
				this.secretDoubleJumpArmedAtMs = now
			}

			return
		}

		if ((now - this.secretDoubleJumpArmedAtMs) > SECRET_DOUBLE_JUMP_TRIGGER_WINDOW_MS)
		{
			this.secretDoubleJumpArmed = false
			this.secretDoubleJumpArmedAtMs = -Infinity
			return
		}

		if (!this.isJumpJustPressed())
		{
			return
		}

		this.secretDoubleJumpUsed = true
		this.secretDoubleJumpArmed = false
		this.secretDoubleJumpArmedAtMs = -Infinity
		this.sprite.setVelocityY(SECRET_DOUBLE_JUMP_VELOCITY)
		this.sprite.play('player-jump', true)
	}

	private resetSecretDoubleJumpState()
	{
		this.jumpStartedAtMs = -Infinity
		this.secretDoubleJumpArmedAtMs = -Infinity
		this.secretDoubleJumpArmed = false
		this.secretDoubleJumpUsed = false
	}

	private isMoveLeftDown(): boolean
	{
		return this.cursors.left.isDown || this.keyA.isDown
	}

	private isMoveRightDown(): boolean
	{
		return this.cursors.right.isDown || this.keyD.isDown
	}

	private isJumpJustPressed(): boolean
	{
		return Phaser.Input.Keyboard.JustDown(this.cursors.up) || Phaser.Input.Keyboard.JustDown(this.keyW)
	}

	private isDownJustPressed(): boolean
	{
		return Phaser.Input.Keyboard.JustDown(this.cursors.down) || Phaser.Input.Keyboard.JustDown(this.keyS)
	}

	private spikeHitOnEnter()
	{
		this.resetSecretDoubleJumpState()
		this.clearCardPowerupOnDamage()

		const isFacingLeft = this.sprite.flipX
		this.sprite.clearTint()
		// Para animaciones direccionales reales (fila derecha/izquierda), evitamos espejado.
		this.sprite.flipX = false
		this.sprite.play(isFacingLeft ? 'player-hit-left' : 'player-hit-right', true)
		this.applyHitKnockback(isFacingLeft)

		this.scene.time.delayedCall(PLAYER_HIT_ANIMATION_DURATION_MS, () => {
			if (this.stateMachine.isCurrentState('spike-hit'))
			{
				this.sprite.flipX = isFacingLeft
				this.stateMachine.setState('idle')
			}
		})

		this.applyEnemyBudgetDamage(DEFAULT_ENEMY_HIT_BUDGET_LOSS)
		events.emit('spike-hit', {
			amount: DEFAULT_ENEMY_HIT_BUDGET_LOSS
		})
	}

	private enemyHitOnEnter()
	{
		this.resetSecretDoubleJumpState()
		this.clearCardPowerupOnDamage()

		const isFacingLeft = this.sprite.flipX
		this.sprite.clearTint()
		// Para animaciones direccionales reales (fila derecha/izquierda), evitamos espejado.
		this.sprite.flipX = false
		this.sprite.play(isFacingLeft ? 'player-hit-left' : 'player-hit-right', true)
		this.applyHitKnockback(isFacingLeft)

		this.scene.time.delayedCall(PLAYER_HIT_ANIMATION_DURATION_MS, () => {
			if (this.stateMachine.isCurrentState('enemy-hit'))
			{
				this.sprite.flipX = isFacingLeft
				this.stateMachine.setState('idle')
			}
		})

		this.applyEnemyBudgetDamage(this.pendingEnemyHitBudgetLoss)
		this.playConfiguredSfx('sfxKeyPlayerHit')
		events.emit('enemy-hit', {
			amount: this.pendingEnemyHitBudgetLoss,
			enemyType: this.pendingEnemyHitEnemyType
		})
		this.pendingEnemyHitBudgetLoss = DEFAULT_ENEMY_HIT_BUDGET_LOSS
		this.pendingEnemyHitEnemyType = 'unknown'
	}

	private applyHitKnockback(isFacingLeft: boolean)
	{
		const body = this.sprite.body as MatterJS.BodyType
		const startX = body.position.x
		const startY = body.position.y
		const direction = isFacingLeft ? 1 : -1
		const targetX = startX + (direction * PLAYER_HIT_KNOCKBACK_PIXELS)

		this.sprite.setVelocity(0, 0)

		this.scene.tweens.addCounter({
			from: 0,
			to: 1,
			duration: PLAYER_HIT_KNOCKBACK_DURATION_MS,
			ease: Phaser.Math.Easing.Sine.Out,
			onUpdate: tween => {
				const progress = tween.getValue()
				const matterBodyApi = (Phaser.Physics.Matter as unknown as {
					Matter: {
						Body: {
							setPosition: (target: MatterJS.BodyType, position: { x: number, y: number }) => void
						}
					}
				}).Matter.Body

				matterBodyApi.setPosition(body, {
					x: Phaser.Math.Linear(startX, targetX, progress),
					y: startY
				})
			}
		})
	}

	private enemyStompOnEnter()
	{
		this.resetSecretDoubleJumpState()

		this.lastStompTimestampMs = this.scene.time.now
		this.sprite.setVelocityY(-10)

		if (this.lastStompableEnemy)
		{
			this.debugEnemyCollisionLog('stomp-enter-neutralize-enemy', this.lastStompableEnemy.body as MatterJS.BodyType)
			// Mark immediately to prevent same-frame collision race before enemy controller updates state.
			this.neutralizeEnemyImmediately(this.lastStompableEnemy)
			this.playConfiguredSfx('sfxKeyEnemyDestroyed')

			const stompBody = this.lastStompableEnemy.body as MatterJS.BodyType
			if (this.obstacles.is('tpv-volador', stompBody))
			{
				events.emit('tpv-volador-stomped', this.lastStompableEnemy)
			}

			if (this.obstacles.is('cajero', stompBody))
			{
				events.emit('cajero-stomped', this.lastStompableEnemy)
			}
		}
		this.lastStompableEnemy = undefined

		this.stateMachine.setState('idle')
	}

	private deadOnEnter()
	{
		this.resetSecretDoubleJumpState()

		const elapsedMs = getPersistentPlayTimeState()?.elapsedMs ?? 0
		const nickname = (this.scene.game.registry.get('playerNickname') as string | undefined) ?? 'SLAYER'
		setPersistentGameOverSummary({
			nickname,
			finalBudget: this.budget,
			finalMaxBudget: this.maxBudget,
			elapsedMs
		})

		this.activeCards.forEach(card => {
			if (card.projectile.active)
			{
				card.projectile.destroy()
			}
		})
		this.activeCards = []

		const body = this.sprite.body as MatterJS.BodyType
		body.collisionFilter.mask = GROUND_COLLISION_CATEGORY
		this.sprite.setSensor(false)
		this.sprite.setIgnoreGravity(false)
		this.sprite.setVelocity(0, 0)
		this.sprite.play('player-death')

		this.sprite.setOnCollide(() => {})

		this.scene.time.delayedCall(1500, () => {
			clearPersistentBudgetState()
			clearPersistentPlayTimeState()
			this.scene.scene.start('daily-ranking', {
				summary: {
					nickname,
					finalBudget: Math.max(0, Math.round(this.budget)),
					finalMaxBudget: Math.max(0, Math.round(this.maxBudget)),
					elapsedMs: Math.max(0, Math.floor(elapsedMs))
				},
				endReason: 'game_over'
			})
		})
	}

	private createAnimations()
	{
		const spriteSource = this.scene.textures.get('penquin').getSourceImage() as { height: number }
		const maxRowIndex = Math.max(0, Math.floor(spriteSource.height / 128) - 1)

		const createRowFrames = (rowIndex: number, frameCount: number) => {
			const safeRowIndex = Phaser.Math.Clamp(rowIndex, 0, maxRowIndex)
			const startSafe = safeRowIndex * PLAYER_SPRITESHEET_COLUMNS
			const endSafe = startSafe + frameCount - 1

			if (safeRowIndex !== rowIndex)
			{
				// console.warn(`[player-anims] row ${rowIndex} fuera de rango. Usando ${safeRowIndex}.`)
			}

			return this.sprite.anims.generateFrameNumbers('penquin', {
				start: startSafe,
				end: endSafe
			})
		}

		this.sprite.anims.create({
			key: 'player-idle',
			frames: [{ key: 'penquin', frame: 0 }]
		})

		this.sprite.anims.create({
			key: 'player-walk',
			frameRate: 10,
			frames: createRowFrames(2, 6),
			repeat: -1
		})

		this.sprite.anims.create({
			key: 'player-jump',
			frameRate: 10,
			frames: createRowFrames(PLAYER_JUMP_ROW_INDEX, 5)
		})

		this.sprite.anims.create({
			key: 'player-jump-landing',
			frames: [{ key: 'penquin', frame: (PLAYER_JUMP_ROW_INDEX * PLAYER_SPRITESHEET_COLUMNS) + PLAYER_JUMP_LANDING_FRAME_OFFSET }]
		})

		this.sprite.anims.create({
			key: 'player-hit-right',
			frameRate: 12,
			frames: createRowFrames(8, 2),
			repeat: 2
		})

		this.sprite.anims.create({
			key: 'player-hit-left',
			frameRate: 12,
			frames: createRowFrames(9, 2),
			repeat: 2
		})

		this.sprite.anims.create({
			key: 'player-throw',
			frameRate: 12,
			frames: createRowFrames(6, 6)
		})

		this.sprite.anims.create({
			key: 'player-coin-right',
			frameRate: PLAYER_COIN_ANIMATION_FRAME_RATE,
			frames: createRowFrames(PLAYER_COIN_RIGHT_ROW_1_BASED - 1, PLAYER_COIN_ANIMATION_FRAMES),
			repeat: PLAYER_COIN_ANIMATION_REPEATS
		})

		this.sprite.anims.create({
			key: 'player-coin-left',
			frameRate: PLAYER_COIN_ANIMATION_FRAME_RATE,
			frames: createRowFrames(PLAYER_COIN_LEFT_ROW_1_BASED - 1, PLAYER_COIN_ANIMATION_FRAMES),
			repeat: PLAYER_COIN_ANIMATION_REPEATS
		})

		this.sprite.anims.create({
			key: 'player-death',
			frames: createRowFrames(10, 6),
			frameRate: 10
		})
	}
}
